import React, { useState, useEffect } from 'react';
import { auth, database, ref, set, onValue, remove, signInWithEmailAndPassword } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Monitor, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

const QRLoginSystem = () => {
  const [view, setView] = useState('desktop'); // 'desktop' or 'mobile'
  const [qrData, setQrData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const [currentUser, setCurrentUser] = useState(null);

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Generate QR code for desktop login
  useEffect(() => {
    if (view === 'desktop' && !currentUser) {
      generateQRCode();
    }
  }, [view, currentUser]);

  // Countdown timer for QR expiry
  useEffect(() => {
    if (view === 'desktop' && countdown > 0 && !scanStatus) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !scanStatus) {
      setScanStatus('expired');
    }
  }, [countdown, view, scanStatus]);

  // Listen for scan confirmation on desktop
  useEffect(() => {
    if (sessionId && view === 'desktop') {
      const sessionRef = ref(database, `qrSessions/${sessionId}`);
      const unsubscribe = onValue(sessionRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          if (data.status === 'scanned') {
            setScanStatus('scanned');
          } else if (data.status === 'confirmed' && data.token) {
            // Sign in with the token
            signInWithCustomToken(auth, data.token)
              .then(() => {
                setScanStatus('success');
                // Clean up session
                remove(sessionRef);
                setTimeout(() => {
                  window.location.href = '/dashboard';
                }, 2000);
              })
              .catch((error) => {
                console.error('Login error:', error);
                setScanStatus('error');
              });
          } else if (data.status === 'rejected') {
            setScanStatus('rejected');
            remove(sessionRef);
          }
        }
      });
      return () => unsubscribe();
    }
  }, [sessionId, view]);

  const generateQRCode = () => {
    const newSessionId = `qr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setCountdown(60);
    setScanStatus(null);

    // Create QR session in database
    const sessionRef = ref(database, `qrSessions/${newSessionId}`);
    set(sessionRef, {
      status: 'pending',
      createdAt: Date.now(),
      expiresAt: Date.now() + 60000, // 60 seconds
    });

    // QR data contains session ID and app URL
    const qrPayload = {
      sessionId: newSessionId,
      type: 'login',
      timestamp: Date.now(),
    };

    setQrData(JSON.stringify(qrPayload));
  };

  const handleScanQR = async () => {
    setIsScanning(true);
    
    // Simulate QR scanner (in production, use a QR scanner library)
    // For demo purposes, we'll use a prompt
    const scannedData = prompt('Paste the QR session ID (in production, this would use camera):');
    
    if (scannedData) {
      try {
        const payload = JSON.parse(scannedData);
        const sessionRef = ref(database, `qrSessions/${payload.sessionId}`);
        
        // Check if session exists and is valid
        const snapshot = await new Promise((resolve) => {
          onValue(sessionRef, resolve, { onlyOnce: true });
        });
        
        const sessionData = snapshot.val();
        
        if (!sessionData) {
          alert('Invalid or expired QR code');
          setIsScanning(false);
          return;
        }

        if (Date.now() > sessionData.expiresAt) {
          alert('QR code has expired');
          setIsScanning(false);
          return;
        }

        // If user is not logged in, prompt for credentials
        if (!currentUser) {
          const email = prompt('Enter your email:');
          if (!email) {
            setIsScanning(false);
            return;
          }
          
          const password = prompt('Enter your password:');
          if (!password) {
            setIsScanning(false);
            return;
          }

          try {
            // Sign in the user
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            // Get the token
            const token = await user.getIdToken();
            
            // Update session with login credentials
            await set(sessionRef, {
              ...sessionData,
              status: 'confirmed',
              token: token,
              email: user.email,
              scannedBy: user.email,
              scannedAt: Date.now(),
              confirmedAt: Date.now(),
            });

            alert('Login successful! The desktop will now be logged in.');
            setIsScanning(false);
            return;
          } catch (error) {
            console.error('Login error:', error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
              alert('Incorrect password. Please try again.');
            } else if (error.code === 'auth/user-not-found') {
              alert('User not found. Please check your email.');
            } else {
              alert('Login failed. Please try again.');
            }
            setIsScanning(false);
            return;
          }
        }

        // If user is already logged in, just confirm
        // Update session status to scanned
        await set(sessionRef, {
          ...sessionData,
          status: 'scanned',
          scannedBy: currentUser.email,
          scannedAt: Date.now(),
        });

        // Show confirmation dialog
        const confirmed = window.confirm(
          `Do you want to log in on this device?\n\nEmail: ${currentUser.email}\n\nClick OK to confirm or Cancel to reject.`
        );

        if (confirmed) {
          // Generate a custom token (in production, this should be done server-side)
          // For demo, we'll use the user's existing auth token
          const token = await currentUser.getIdToken();
          
          await set(sessionRef, {
            ...sessionData,
            status: 'confirmed',
            token: token,
            confirmedAt: Date.now(),
          });

          alert('Login confirmed! The desktop will now be logged in.');
        } else {
          await set(sessionRef, {
            ...sessionData,
            status: 'rejected',
            rejectedAt: Date.now(),
          });
          alert('Login rejected.');
        }
      } catch (error) {
        console.error('Scan error:', error);
        alert('Error processing QR code');
      }
    }
    
    setIsScanning(false);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    card: {
      background: '#fff',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#2d3748',
      marginBottom: '10px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#718096',
      marginBottom: '30px',
    },
    toggleContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      background: '#f7fafc',
      padding: '5px',
      borderRadius: '12px',
    },
    toggleButton: {
      flex: 1,
      padding: '12px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    toggleButtonActive: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    toggleButtonInactive: {
      background: 'transparent',
      color: '#4a5568',
    },
    qrContainer: {
      background: '#fff',
      padding: '20px',
      borderRadius: '16px',
      border: '3px solid #e2e8f0',
      marginBottom: '20px',
      display: 'inline-block',
    },
    countdown: {
      fontSize: '14px',
      color: '#718096',
      marginTop: '15px',
      fontWeight: '600',
    },
    statusContainer: {
      padding: '20px',
      borderRadius: '12px',
      marginTop: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      fontWeight: '600',
    },
    statusSuccess: {
      background: '#c6f6d5',
      color: '#2f855a',
    },
    statusError: {
      background: '#fed7d7',
      color: '#c53030',
    },
    statusScanned: {
      background: '#bee3f8',
      color: '#2c5282',
    },
    button: {
      width: '100%',
      padding: '14px',
      fontSize: '16px',
      fontWeight: '700',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      color: '#fff',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      transition: 'all 0.3s ease',
      marginTop: '20px',
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
    },
    instructionsList: {
      textAlign: 'left',
      marginTop: '20px',
      padding: '20px',
      background: '#f7fafc',
      borderRadius: '12px',
      lineHeight: '1.8',
    },
    instructionItem: {
      marginBottom: '12px',
      color: '#4a5568',
      fontSize: '14px',
    },
  };

  const [buttonHover, setButtonHover] = useState(false);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>🔐 QR Login System</h1>
        <p style={styles.subtitle}>
          {view === 'desktop' 
            ? 'Scan this QR code with your mobile device' 
            : 'Use your mobile to scan and authenticate'}
        </p>

        {/* Toggle between Desktop and Mobile view */}
        <div style={styles.toggleContainer}>
          <button
            style={{
              ...styles.toggleButton,
              ...(view === 'desktop' ? styles.toggleButtonActive : styles.toggleButtonInactive),
            }}
            onClick={() => setView('desktop')}
          >
            <Monitor size={18} />
            Desktop View
          </button>
          <button
            style={{
              ...styles.toggleButton,
              ...(view === 'mobile' ? styles.toggleButtonActive : styles.toggleButtonInactive),
            }}
            onClick={() => setView('mobile')}
          >
            <Smartphone size={18} />
            Mobile View
          </button>
        </div>

        {/* Desktop View - Show QR Code */}
        {view === 'desktop' && (
          <>
            {!scanStatus && qrData && (
              <>
                <div style={styles.qrContainer}>
                  <QRCodeSVG 
                    value={qrData} 
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <div style={styles.countdown}>
                  ⏱️ Expires in: {countdown}s
                </div>
                <button
                  style={{
                    ...styles.button,
                    ...(buttonHover ? styles.buttonHover : {}),
                  }}
                  onClick={generateQRCode}
                  onMouseEnter={() => setButtonHover(true)}
                  onMouseLeave={() => setButtonHover(false)}
                >
                  <RefreshCw size={18} style={{ display: 'inline', marginRight: '8px' }} />
                  Regenerate QR Code
                </button>
              </>
            )}

            {scanStatus === 'scanned' && (
              <div style={{ ...styles.statusContainer, ...styles.statusScanned }}>
                <CheckCircle size={24} />
                QR Code Scanned! Waiting for confirmation...
              </div>
            )}

            {scanStatus === 'success' && (
              <div style={{ ...styles.statusContainer, ...styles.statusSuccess }}>
                <CheckCircle size={24} />
                Login Successful! Redirecting...
              </div>
            )}

            {scanStatus === 'rejected' && (
              <div style={{ ...styles.statusContainer, ...styles.statusError }}>
                <XCircle size={24} />
                Login Rejected
              </div>
            )}

            {scanStatus === 'expired' && (
              <div style={{ ...styles.statusContainer, ...styles.statusError }}>
                <XCircle size={24} />
                QR Code Expired
              </div>
            )}

            {scanStatus === 'error' && (
              <div style={{ ...styles.statusContainer, ...styles.statusError }}>
                <XCircle size={24} />
                Login Error. Please try again.
              </div>
            )}

            <div style={styles.instructionsList}>
              <div style={styles.instructionItem}>
                1️⃣ Open the app on your mobile device
              </div>
              <div style={styles.instructionItem}>
                2️⃣ Make sure you're logged in
              </div>
              <div style={styles.instructionItem}>
                3️⃣ Switch to "Mobile View" and scan this QR
              </div>
              <div style={styles.instructionItem}>
                4️⃣ Confirm the login on your mobile
              </div>
            </div>
          </>
        )}

        {/* Mobile View - Scan QR Code */}
        {view === 'mobile' && (
          <>
            <div style={{
              padding: '20px',
              background: '#f7fafc',
              borderRadius: '12px',
              marginBottom: '20px',
            }}>
              {currentUser ? (
                <>
                  <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '10px' }}>
                    Logged in as:
                  </p>
                  <p style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600' }}>
                    {currentUser.email}
                  </p>
                </>
              ) : (
                <>
                  <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '10px' }}>
                    Status:
                  </p>
                  <p style={{ color: '#e53e3e', fontSize: '16px', fontWeight: '600' }}>
                    Not logged in
                  </p>
                  <p style={{ color: '#718096', fontSize: '12px', marginTop: '8px' }}>
                    You can still scan QR codes to login
                  </p>
                </>
              )}
            </div>

            <button
              style={{
                ...styles.button,
                ...(buttonHover ? styles.buttonHover : {}),
              }}
              onClick={handleScanQR}
              disabled={isScanning}
              onMouseEnter={() => setButtonHover(true)}
              onMouseLeave={() => setButtonHover(false)}
            >
              {isScanning ? 'Processing...' : '📷 Scan QR Code to Login'}
            </button>

            <div style={styles.instructionsList}>
              <div style={styles.instructionItem}>
                📱 In production, this would open your camera to scan the QR code
              </div>
              <div style={styles.instructionItem}>
                🔐 For this demo, copy the session ID from the Desktop View QR
              </div>
              {currentUser ? (
                <div style={styles.instructionItem}>
                  ✅ You're logged in - confirm or reject the login request
                </div>
              ) : (
                <div style={styles.instructionItem}>
                  🔑 Not logged in? Enter your credentials after scanning to login
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QRLoginSystem;